// Pure helpers for onboarding rounds — unit-testable, no Firestore.

import { PINNED_SET_NAME_MATCH } from './onboardingConfig';
import { copy } from './copy';

/** Normalize legacy string or array into a string[]. */
export function normalizeCollectorTypes(raw) {
  if (Array.isArray(raw)) return raw.filter((id) => typeof id === 'string' && id);
  if (typeof raw === 'string' && raw) return [raw];
  return [];
}

export function hasSetBuilder(collectorTypes) {
  return normalizeCollectorTypes(collectorTypes).includes('set_builder');
}

/** Toggle id in a multi-select list (order preserved; add appends). */
export function toggleId(list, id) {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id];
}

/**
 * AuthContext bootstrap guard: only true first-time social sign-ins should get
 * a fresh `onboarding: in_progress` doc written. A transient/failed profile
 * fetch (network blip, timeout) must NEVER be treated the same as "this uid
 * has no users/{uid} doc yet" — doing so re-writes a fresh onboarding+
 * profileCompleted:false record over an existing, completed user on every
 * re-auth (e.g. the session's first ID-token refresh), silently resetting
 * them back to onboarding step 0. Only bootstrap when the read genuinely
 * succeeded and came back empty.
 */
export function shouldBootstrapSocialProfile({ profile, profileFetchFailed, providerId } = {}) {
  if (profile) return false;
  if (profileFetchFailed) return false;
  return providerId === 'google.com' || providerId === 'facebook.com';
}

export function nameOfSet(s) {
  return s?.SetName || s?.name || s?.setName || s?.id || '';
}

/** Display name used for ranking / suggestion filters. */
export function fighterDisplayName(fighter) {
  if (!fighter) return '';
  return (fighter.name || `${fighter.firstName || ''} ${fighter.lastName || ''}`.trim()).trim();
}

// Keep out of R4 quick-picks (still searchable). Ace / Ace SP = dirty suffix docs;
// Aaron Rosa surfaces via A-name alphabetical tie-break on low scores.
const R4_SUGGESTION_EXCLUDES = new Set(['aaron rosa', 'ace', 'ace sp']);

export function isExcludedFromR4Suggestions(fighter) {
  const name = fighterDisplayName(fighter).toLowerCase();
  return Boolean(name) && R4_SUGGESTION_EXCLUDES.has(name);
}

/**
 * Score a fighter for "popularity" quick-picks.
 * Signals (best → weakest): isFeatured, curated fallback order, ranking, cardCount.
 */
export function fighterPopularityScore(fighter, fallbackNames = copy.fallbackFighters) {
  if (!fighter) return 0;
  let score = 0;
  if (fighter.isFeatured) score += 1000;
  const name = fighterDisplayName(fighter);
  const idx = fallbackNames.findIndex((n) => n.toLowerCase() === (name || '').toLowerCase());
  if (idx >= 0) score += 500 - idx;
  if (typeof fighter.ranking === 'number' && fighter.ranking > 0) {
    score += Math.max(0, 40 - fighter.ranking);
  }
  const cards = fighter.cardCount || fighter.cardsCount || fighter.totalCards || 0;
  if (typeof cards === 'number') score += Math.min(cards, 200);
  return score;
}

/** Top N fighters by popularity (stable name tie-break). */
export function topFightersByPopularity(fighters, n = 12) {
  if (!Array.isArray(fighters) || fighters.length === 0) return [];
  return [...fighters]
    .filter((f) => !isExcludedFromR4Suggestions(f))
    .sort((a, b) => {
      const d = fighterPopularityScore(b) - fighterPopularityScore(a);
      if (d !== 0) return d;
      return fighterDisplayName(a).localeCompare(fighterDisplayName(b));
    })
    .slice(0, n);
}

/**
 * Suggest 3–5 chase sets: pinned (Knockout) first, then newest releaseYear.
 * Optionally boost sets whose name mentions a favorite fighter last name (cheap heuristic).
 */
export function suggestChaseSets(sets, { favoriteFighterNames = [], count = 5 } = {}) {
  if (!Array.isArray(sets) || sets.length === 0) return [];
  const n = Math.max(3, Math.min(5, count));
  const names = (favoriteFighterNames || [])
    .map((name) => String(name || '').trim().toLowerCase())
    .filter(Boolean);
  const lastNames = names
    .map((full) => full.split(/\s+/).pop())
    .filter((ln) => ln && ln.length > 2);

  const scored = sets.map((s, i) => {
    const name = nameOfSet(s);
    let score = (s.releaseYear || 0) * 10;
    if (PINNED_SET_NAME_MATCH.test(name)) score += 10_000;
    if (lastNames.some((ln) => name.toLowerCase().includes(ln))) score += 500;
    // Stable tie-break: original index inverted so earlier docs win ties.
    score += (sets.length - i) * 0.001;
    return { s, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, n).map((x) => x.s);
}

/** Client-filter sets by name (case-insensitive substring). */
export function filterSetsByQuery(sets, q) {
  if (!Array.isArray(sets)) return [];
  const term = (q || '').trim().toLowerCase();
  if (!term) return sets;
  return sets.filter((s) => nameOfSet(s).toLowerCase().includes(term));
}

/** Split a full display name into {firstName, lastName} (naive last-space split). */
export function splitFighterName(fullName) {
  const trimmed = String(fullName || '').trim();
  if (!trimmed) return { firstName: '', lastName: '' };
  const parts = trimmed.split(/\s+/);
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

/**
 * Normalize an R4 pick (live fighter doc or curated fallback) into the
 * canonical `favoriteFighters` shape shared with Profile / FavoritesManager /
 * publicProfiles — R4 is the only onboarding write path for fighter
 * favorites, so this is the one place that shape gets built.
 */
export function toCanonicalFighter(f) {
  if (!f || !f.id) return null;
  const hasSplitName = f.firstName || f.lastName;
  const { firstName, lastName } = hasSplitName
    ? { firstName: f.firstName || '', lastName: f.lastName || '' }
    : splitFighterName(f.name);
  return {
    id: f.id,
    firstName,
    lastName,
    weightClass: f.weightClass || '',
    profileURL: f.profileURL || f.photoURL || '',
  };
}

/**
 * Build the merge-write patch for onboarding step-save/completion. Only
 * persists fields that have a value (avoid clobbering with empties).
 * collectorTypes is always written (including []) so "none" survives resume.
 * favoriteFighters is the single canonical fighter-favorites field — same one
 * Profile / FavoritesManager / publicProfiles read and write.
 * `existingChaseTargets` (already-hydrated CHASE list) lets the R5 pick land
 * in CHASE without stomping any pins a returning user already has.
 */
export function buildOnboardingProfilePatch(data, existingChaseTargets = []) {
  const out = {};
  if (data.displayName && data.displayName.trim()) out.displayName = data.displayName.trim();
  if (Array.isArray(data.collectorTypes)) out.collectorTypes = data.collectorTypes;
  if (Array.isArray(data.favoriteFighters) && data.favoriteFighters.length) {
    out.favoriteFighters = data.favoriteFighters;
  }
  if (data.activeChaseSetId) {
    out.activeChaseSetId = data.activeChaseSetId;
    const alreadyChased = (existingChaseTargets || []).some(
      (t) => t.id === data.activeChaseSetId || t.setId === data.activeChaseSetId
    );
    if (!alreadyChased) {
      out.chaseTargets = [
        ...(existingChaseTargets || []),
        {
          id: data.activeChaseSetId,
          setId: data.activeChaseSetId,
          insertKey: null,
          label: data.activeChaseSetName || null,
          addedAt: Date.now(),
        },
      ];
    }
  }
  return out;
}

const onboardingHelpersExport = {
  normalizeCollectorTypes,
  hasSetBuilder,
  toggleId,
  shouldBootstrapSocialProfile,
  nameOfSet,
  fighterDisplayName,
  isExcludedFromR4Suggestions,
  fighterPopularityScore,
  topFightersByPopularity,
  suggestChaseSets,
  filterSetsByQuery,
  splitFighterName,
  toCanonicalFighter,
  buildOnboardingProfilePatch,
};

export default onboardingHelpersExport;
